import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../api/axios';
import { toast } from 'react-toastify';
import { clientQueryKeys } from './useClients';
export type SubscriptionResponse = Subscription[];

export interface Subscription {
  banner_text: string;
  cents_per_minute: number;
  code: any;
  description: string;
  details: string;
  discount?: number;
  id: any;
  minutes: number;
  name: string;
  price: number;
  package_id: number;
}

const getDefaultSubscriptions = async (): Promise<SubscriptionResponse> => {
  return await axiosGet('/subscriptions/default_templates', {}, 'v3').then(
    (defaultSubscriptionsResponse) => defaultSubscriptionsResponse.data,
  );
};

export const useDefaultSubscriptions = () => {
  return useQuery<SubscriptionResponse>(['default_subscriptions'], () => getDefaultSubscriptions());
};

const createSubscriptionRequest = async (payload: Partial<Subscription>): Promise<Subscription> => {
  return await axiosPost('/subscriptions', payload, 'v3').then((subscriptionResponse) => subscriptionResponse.data);
};

export const useCreateSubscription = (clientId: number) => {
  const queryClient = useQueryClient();
  const createSubscription = useMutation(
    ({
      payload,
      bypassToast,
      byPassInvalidateQuery,
    }: {
      payload: Partial<Subscription>;
      bypassToast: boolean;
      byPassInvalidateQuery: boolean;
    }) => createSubscriptionRequest(payload),
    {
      onSuccess: (_, variables) => {
        if (!variables.byPassInvalidateQuery) {
          queryClient.invalidateQueries(['default_subscriptions']);
          queryClient.invalidateQueries(['clients', clientId]);
        }
        if (!variables.bypassToast) {
          toast.success('Subscription created successfully');
        }
      },
    },
  );

  return { createSubscription };
};

const updateSubscriptionRequest = async (
  subscription_id: number,
  payload: Partial<Subscription>,
): Promise<Subscription> => {
  return await axiosPut(`/subscriptions/${subscription_id}`, payload, 'v3').then(
    (subscriptionResponse) => subscriptionResponse.data,
  );
};

export const useUpdateSubscription = (clientId: number) => {
  const queryClient = useQueryClient();
  const updateSubscription = useMutation(
    ({ subscription_id, payload }: { subscription_id: number; payload: Partial<Subscription> }) =>
      updateSubscriptionRequest(subscription_id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['default_subscriptions']);
        queryClient.invalidateQueries(['clients', clientId]);
        toast.success('Subscription updated successfully');
      },
    },
  );

  return { updateSubscription };
};

const removeSubscriptionRequest = async (subscription_id: number): Promise<Subscription> => {
  return await axiosDelete(`/subscriptions/${subscription_id}`, 'v3').then(
    (subscriptionResponse) => subscriptionResponse.data,
  );
};

export const useRemoveSubscription = (clientId: number) => {
  const queryClient = useQueryClient();
  const removeSubscription = useMutation(
    ({ subscription_id }: { subscription_id: number }) => removeSubscriptionRequest(subscription_id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['default_subscriptions']);
        queryClient.invalidateQueries(['clients', clientId]);
        toast.success('Subscription removed successfully');
      },
    },
  );

  return { removeSubscription };
};
